'use client';

import { FeaturedPropertiesCarousel as CoreFeaturedPropertiesCarousel } from '../../../../src/components/properties';
import type { Property } from '../../../../src/lib/api/clients/properties';
import type { FeaturedPlacementSummary } from '../../../../src/lib/api/clients/featured-content';
import { isHomepagePlacement } from '../../../../src/lib/api/clients/featured-content';

interface PropertyWithFeatured extends Property {
  featuredPlacements?: FeaturedPlacementSummary[];
}

export interface RouteRoyalFeaturedPropertiesCarouselProps {
  properties: PropertyWithFeatured[];
  title?: string;
  subtitle?: string;
  onPropertyClick?: (property: PropertyWithFeatured) => void;
  maxItems?: number;
  messages?: Record<string, unknown>;
  locale?: 'en' | 'es' | 'fr';
}

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

function getHomepageFeaturedProperties(
  properties: PropertyWithFeatured[],
  maxCount: number,
): PropertyWithFeatured[] {
  const homepageFeatured = properties.filter(p =>
    p.featuredPlacements?.some(fp => isHomepagePlacement(fp.placementType)),
  );
  const sorted = homepageFeatured.sort((a, b) => {
    const getPriority = (prop: PropertyWithFeatured) => {
      const placement = prop.featuredPlacements?.find(fp => isHomepagePlacement(fp.placementType));
      return placement?.priority || 0;
    };
    return getPriority(b) - getPriority(a);
  });
  return sorted.slice(0, maxCount);
}

export function RouteRoyalFeaturedPropertiesCarousel({
  properties,
  title,
  subtitle,
  onPropertyClick,
  maxItems = 6,
  messages,
  locale = 'en',
}: RouteRoyalFeaturedPropertiesCarouselProps) {
  const carouselMsg = messages?.featuredCarousel as Record<string, unknown> | undefined;
  const displayTitle = title || safeGetString(carouselMsg?.title, 'Featured Properties');
  const displaySubtitle = subtitle || safeGetString(carouselMsg?.subtitle, 'Explore our curated selection of exceptional properties');

  const homepageProperties = getHomepageFeaturedProperties(properties, maxItems);

  if (homepageProperties.length === 0) return null;

  return (
    <CoreFeaturedPropertiesCarousel
      properties={homepageProperties}
      title={displayTitle}
      subtitle={displaySubtitle}
      variant="fullwidth"
      className="bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a]"
      maxItems={maxItems}
      locale={locale}
      {...(messages && { messages })}
      {...(onPropertyClick && { onPropertyClick })}
    />
  );
}
